import { Component, Injector } from '@angular/core';
import type { OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { ServiceAreasService, FirebazeStorageService } from '@shared/services';
import { CheckboxModalPage } from '@shared/pages';
import { ViewBasePage } from '@shared/pages';
import type { TCoordinate, TDealership, TRegionDocument, TServiceArea /* TZipcodes */ } from '@shared/models';

type TZip = {
    'city': string;
    'distance': number;
    'zipcode': string;
    'regionName': string;
    'regionCode': string;
    'uid': string;
};

type TSelectable = {
    'line1': string;
    'line2': string;
    'name': string;
    'zipcode': string;
    'city': string;
    'distance': number;
    'regionName': string;
    'regionCode': string;
    'uid': string;
};
@Component({
    'selector': 'app-serviceAreaMultiple',
    'templateUrl': './serviceAreaMultiple.page.html',
    'styleUrls': ['./serviceAreaMultiple.page.scss'],
    'standalone': true,
    'imports': [ CommonModule, FormsModule, IonicModule ]
})
export class ServiceAreaMultiplePage extends ViewBasePage implements OnInit {
    private dealership: TDealership;
    private area = {};
    public serviceArea: any = {
        'radius': 5,
        'radiusStr': '5 Miles'
    };
    private regionInfo: TRegionDocument;
    public selectedZips: any = [];

    constructor(private serviceAreasService: ServiceAreasService,
        private firebazeStorageService: FirebazeStorageService, injector: Injector) {
        super(injector);
    }

    public ionViewWillEnter(): void {
        super.setCurrentView(this.Constants.VIEW_SERVICE_AREAS_MULTIPLE);
    }

    public override async ngOnInit(): Promise<void> {
        this.dealership = this.sessionService.getDealership();

        await this.uxHelperService.showSpinner();
        this.regionInfo = await this.firebazeStorageService.getRegionDocument(this.dealership.address.regionCode);
        await this.uxHelperService.hideSpinner();
    }

    // public async onStateChange(statecode: string): Promise<void> {
    //     await this.uxHelperService.showSpinner();
    //     this.regionInfo = await this.firebazeStorageService.getStateDocument(statecode);
    //     // this.area.xstate = this.stateInfo.xstate;
    //     // this.area.city = '';
    //     // this.area.distance = 0;
    //     // this.area.zipcode = '';
    //     await this.uxHelperService.hideSpinner();
    // }

    // public initMoreMenu(): void {
    //     this.resetPopoverMenu();
    //     this.pushPopoverMenuItem('Show Tour', 'show-service-area-multiple-tour');
    // }

    // private loadViewData() {
    //     // if (this.uid) {
    //     //     this.serviceAreasService.getByUid(this.uid)
    //     //     .then((area) => {
    //     //         this.area = area;
    //     //     });
    //     // }
    // }

    public async doFindZipcodes() {
        try {
            await this.uxHelperService.showSpinner('Checking thousands of zipcodes.');
            const serviceAreas = this.getNearbyZipcodes(
                this.dealership.address.coordinate, this.serviceArea.radius);
            // this.dealershipStatecode, this.dealershipCoords, this.serviceArea.radius);
            await this.uxHelperService.hideSpinner();
            const selectables = this.formatServiceAreas(serviceAreas);
            this.openCheckboxModal(selectables /*, serviceAreas */);
        } catch (err) {
            await this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        }
    }

    public getNearbyZipcodes(coordinate: TCoordinate, radius: number): TZip[] {
        const nearbyZipcodes: TZip[] = [];

        this.regionInfo.zipcodes.filter(zipItem => {
            const dist = this.utilsService.calculateDistance(coordinate, zipItem.coordinate);
            if (dist.miles <= radius) {
                const zip: TZip = {
                    'city': zipItem.city,
                    'distance': dist.miles,
                    'regionName': this.regionInfo.state,
                    'regionCode': this.regionInfo.statecode,
                    'zipcode': zipItem.zipcode,
                    'uid': zipItem.zipcode
                };
                nearbyZipcodes.push(zip);
            }
        });

        return nearbyZipcodes;
    }

    private formatServiceAreas(serviceAreas: TZip[]): TSelectable[] {
        const selectables: TSelectable[] = [];

        serviceAreas.forEach((area) => {
            const line1 = area.zipcode + ' - ' + area.city;
            const line2 = area.distance + ' miles away';
            const selectable: TSelectable = {
                'line1': line1,
                'line2': line2,
                'name': line1 + '[' + line2 + ']',
                'zipcode': area.zipcode,
                'city': area.city,
                'distance': area.distance,
                'regionName': area.regionName,
                'regionCode': area.regionCode,
                'uid': area.uid
            };
            selectables.push(selectable);
        });
        return selectables;
    }

    private async openCheckboxModal(selectables: TSelectable[]) {
        const options: any = {
            'component': CheckboxModalPage,
            'componentProps': {
                'selectables': selectables,
                'title': 'Select Service Areas',
                'showTestCustomerButton': false
            },
            'backdropDismiss': true,
            'showBackdrop': true,
            'cssClass': 'serviceAreaMultipleCheckboxModal'
        };

        const myModal = await this.modalController.create(options);
        myModal.onDidDismiss()
            .then((response: any) => {
                if (response.data.okay) {
                    this.selectedZips = response.data.selectedItems;
                }
            });

        myModal.present();
    }

    public onRadiusChange() {
        this.serviceArea.radiusStr = this.serviceArea.radius + ' Miles';
    }

    public async onSaveClick() {
        try {
            if (this.selectedZips.length > 0) {
                await this.uxHelperService.showSpinner('Saving.');
                this.selectedZips.forEach(async (zip) => {
                    // Do not add this service area to the database if it is already in the database.
                    const check: TServiceArea = await this.serviceAreasService.getByUid(zip.uid);
                    if (!check) {
                        const area: TServiceArea = {
                            'city': zip.city,
                            'distance': zip.distance,
                            'regionName': zip.regionName,
                            'regionCode': zip.regionCode,
                            'zipcode': zip.zipcode,
                            'uid': zip.uid
                        };
                        this.serviceAreasService.set(area);
                    }
                });
                await this.uxHelperService.hideSpinner();
                this.uxHelperService.showToast('Saved.');
            }
        } catch (err) {
            await this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error:', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
            this.router.navigateByUrl('/managerSettings/serviceAreas');
        }
    }
}
